import { Direction } from 'matrix-js-sdk/src';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useRoomIdentityContext from 'contexts/RoomIdentityContext/useRoomIdentityContext';
import useDefinedMatrixValues from 'hooks/useDefinedMatrixValues';
import logger from 'utils/logger';
import { getFilter } from 'utils/matrixUtils';
import { sleep } from 'utils/utils';

import {
  REACTION_MAX_FETCH_ATTEMPTS_COUNT,
  REACTION_AGE_LIMIT_MINUTES,
  REACTION_MESSAGE_LIMIT,
  REACTIONS_FILTER,
} from './utils';

const useReactionHistory = () => {
  const {
    state: { roomId },
  } = useRoomIdentityContext();
  const { matrixRoom } = useDefinedMatrixValues();
  const { matrixClient } = useMatrixContext();
  const paginationToken = useRef<string | null>(null);

  const filter = useMemo(() => getFilter(REACTIONS_FILTER, matrixClient.getUserId()), [matrixClient]);

  useEffect(() => {
    const fetchAllReactions = async () => {
      const reactionAgeLimit = moment().subtract(REACTION_AGE_LIMIT_MINUTES, 'minutes');
      let attempt = 1;
      let hasMore = true;

      while (hasMore && attempt <= REACTION_MAX_FETCH_ATTEMPTS_COUNT) {
        try {
          const { chunk, end } = await matrixClient.createMessagesRequest(
            roomId,
            paginationToken.current,
            REACTION_MESSAGE_LIMIT,
            Direction.Backward,
            filter
          );

          if (!chunk?.length) {
            logger.info('No more reactions to fetch from timeline');
            break;
          }

          const eventMapper = matrixClient.getEventMapper({ decrypt: true });
          const newMatrixEvents = chunk.map(eventMapper);
          const lastReactionEvent = newMatrixEvents[newMatrixEvents.length - 1];

          if (!lastReactionEvent?.localTimestamp) {
            logger.error('Reaction event in chunk is not valid, stopping reaction history fetch');
            break;
          }
          const dateOfLastEvent = moment(lastReactionEvent.localTimestamp);

          matrixRoom.addEventsToTimeline(newMatrixEvents, true, true, matrixRoom.getLiveTimeline(), end);
          paginationToken.current = end || null;

          if (dateOfLastEvent.isAfter(reactionAgeLimit)) {
            logger.info(`Last reaction event is within ${REACTION_AGE_LIMIT_MINUTES} minutes. Fetching next batch`);
          } else {
            logger.info(`Last reaction event is older than ${REACTION_AGE_LIMIT_MINUTES} minutes. Stopping fetch`);
            hasMore = false;
          }
        } catch (error) {
          attempt += 1;

          if (attempt > REACTION_MAX_FETCH_ATTEMPTS_COUNT) {
            logger.error('Max fetch attempts reached. Stopping reaction history fetch.');
          } else {
            logger.warn(`Retrying reaction history fetch... (${attempt}/${REACTION_MAX_FETCH_ATTEMPTS_COUNT})`);
            await sleep(1000 * attempt);
          }
        }
      }
    };

    fetchAllReactions();
  }, [matrixClient, matrixRoom, roomId, filter]);
};

export default useReactionHistory;
